import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, CircleIcon } from '@phosphor-icons/react';
import type { AllDiffusionRecipientDto } from '@monorepo/shared';
import { BaseModal } from '../ui/BaseModal';
import { useGetAllRecipients } from '../../services/documents.service';

interface DocumentReadersModalProps {
  companyId: string;
}

export interface DocumentReadersModalRef {
  open: (documentId: string, documentName: string) => void;
}

const formatDate = (date: Date | string | null): string =>
  date ? new Date(date).toLocaleDateString('fr-FR') : '';

const ReaderRow = ({ recipient }: { recipient: AllDiffusionRecipientDto }) => {
  const { t } = useTranslation();
  const { firstName, lastName, email, hasOpened, firstOpenedAt } = recipient;

  return (
    <li className="flex items-start gap-3 border-b border-gray-100 py-2 last:border-b-0">
      {hasOpened ? (
        <CheckCircleIcon size={20} weight="fill" className="mt-0.5 flex-shrink-0 text-green-600" />
      ) : (
        <CircleIcon size={20} className="mt-0.5 flex-shrink-0 text-gray-300" />
      )}
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-gray-900">
          {firstName} {lastName}
        </div>
        <div className="truncate text-xs text-gray-500">{email}</div>
        {hasOpened && firstOpenedAt && (
          <div className="text-xs text-green-700">
            {t('documents.readers_modal.first_opened_at', { date: formatDate(firstOpenedAt) })}
          </div>
        )}
      </div>
    </li>
  );
};

export const DocumentReadersModal = forwardRef<DocumentReadersModalRef, DocumentReadersModalProps>(
  ({ companyId }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [documentId, setDocumentId] = useState<string | null>(null);
    const [documentName, setDocumentName] = useState('');

    const { data, isLoading } = useGetAllRecipients(companyId, isOpen ? (documentId ?? '') : '');

    useImperativeHandle(ref, () => ({
      open: (id: string, name: string) => {
        setDocumentId(id);
        setDocumentName(name);
        setIsOpen(true);
      },
    }));

    const recipients = data?.recipients ?? [];
    const opened = recipients.filter((recipient) => recipient.hasOpened);
    const notOpened = recipients.filter((recipient) => !recipient.hasOpened);

    const footer = (
      <button
        type="button"
        onClick={() => setIsOpen(false)}
        className="w-full rounded-md bg-gray-100 px-4 py-2 font-medium text-gray-900 transition hover:bg-gray-200"
      >
        {t('documents.readers_modal.close')}
      </button>
    );

    return (
      <BaseModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('documents.readers_modal.title')}
        footer={footer}
        maxWidth="lg"
      >
        <div className="flex flex-col gap-4">
          <p className="text-base font-medium text-gray-900">{documentName}</p>

          {isLoading && <p className="text-sm text-gray-500">{t('common.loading')}</p>}

          {!isLoading && recipients.length === 0 && (
            <p className="text-sm text-gray-500">{t('documents.readers_modal.empty')}</p>
          )}

          {!isLoading && recipients.length > 0 && (
            <>
              <p className="text-sm text-gray-600">
                {t('documents.readers_modal.summary', {
                  opened: opened.length,
                  total: recipients.length,
                })}
              </p>

              {notOpened.length > 0 && (
                <div className="flex flex-col gap-1">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('documents.readers_modal.not_opened')}
                  </h3>
                  <ul className="flex flex-col">
                    {notOpened.map((recipient) => (
                      <ReaderRow key={recipient.userCompanyId} recipient={recipient} />
                    ))}
                  </ul>
                </div>
              )}

              {opened.length > 0 && (
                <div className="flex flex-col gap-1">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('documents.readers_modal.opened')}
                  </h3>
                  <ul className="flex flex-col">
                    {opened.map((recipient) => (
                      <ReaderRow key={recipient.userCompanyId} recipient={recipient} />
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </BaseModal>
    );
  },
);

DocumentReadersModal.displayName = 'DocumentReadersModal';
